import React, { useState, useEffect, useMemo } from "react";
import ZInput from "@molecules/ZInput";
import ZInputNumber from "@atoms/ZInputNumber";
import ZSelect from "@atoms/ZSelect";
import ZRadioGroup from "@atoms/ZRadioGroup";
import ZTypography, { FontWeights } from "@atoms/ZTypography";
import ZModal from "@atoms/ZModal";
import Form from "antd/lib/form";
import ZTable from "@organisms/ZTable";
import ZDatePicker from "@molecules/ZDatePicker";
import {
	CommonServerStatusEnum,
	TableQueryFilter,
	ZomentumEntities,
	ZomentumWindow
} from "@/Contracts";
import { useSelector } from "react-redux";
import useDispatch from "@/Components/Utils/useDispatch";
import DocumentsActionCreators, {
	DocumentDetailsSuccessfulAction
} from "@/Actions/Documents";
import {
	MergeTagInput,
	MergeTagPreference,
	MergeTagVariable,
	DocumentRecipient,
	MergeTagVariableDataType,
	BulkAssignSignatureBlocksRequest,
	EditDocumentDetailsRequest,
	EditDocumentRequest,
	UserType,
	MissingMandatoryFieldsFormValues
} from "@/Contracts/Documents";
import { convertToISODate } from "@/Utils";
import filter from "lodash/filter";
import {
	Block,
	BlockType,
	TextBlockData
} from "@/Contracts/ProposalBuilder/Block";
import {
	capitalizeFirstLetter,
	ChildEntityHydrationParams,
	filterSelectByTextInput
} from "@/Contracts/Utils";
import BlocksActionCreators from "@/ProposalBuilder/store/actions/Blocks";
import ZMessage from "@molecules/ZMessage";
import {
	ClientContact,
	ClientContactBulkUpdate,
	ClientContactBulkUpdateRequest,
	ClientContactRequest,
	ClientsRequest
} from "@/Contracts/Clients";
import MissingDetailsSection from "@molecules/ZMissingEmailSection";
import { ClientsActionCreators } from "@/Actions/Clients";
import { IEmptyMergeTagsModalProps } from "./interface";
import ZRenderCustomFieldFormItem from "@organisms/ZRenderCustomFieldFormItem";
import debounce from "lodash/debounce";
import uniq from "lodash/uniq";
import ZClientSelect from "@organisms/ZClientSelect";
import ZUserSelect from "@organisms/ZUserSelect";
import { ManagerAndAdminRoleOnly } from "@/Contracts/Users";
import { EInputWidths } from "@molecules/ZInput/interface";
import ZForm from "@organisms/ZForm";
import {
	EWidgets,
	IFormFieldProps,
	IFormProps
} from "@organisms/ZForm/interface";
import {
	checkMandatoryDocumentSystemFields,
	getSigneesAndInputFieldsWithInactiveUser
} from "@/V2Utils";
import { DocumentApprovalRuleActionCreators } from "@/Actions/DocumentApproval";
import { DocumentApprovalUpdateRequest } from "@/Contracts/DocumentApprovals";
import { GlobalState } from "@/Reducers/interface";
import { useProposalBuilderDispatch } from "@/ProposalBuilder/store";
import { processCustomFieldFormValues } from "@/Utils/common";
import { useFeatureFlag } from "@/Hooks/useFeature";
import { CKEditorInstanceManager } from "@/CKEditorComponent/Editor/utils/CKEditorInstanceManager";
const { ZText, ZSectionHeading } = ZTypography;

declare let window: ZomentumWindow;

const EmptyMergeTagsAlertModal: React.FC<IEmptyMergeTagsModalProps> = props => {
	const proposalBuilderDispatch = useProposalBuilderDispatch();
	const {
		allMergeTags,
		documentId,
		allRecipients,
		allSignees,
		recipientsMissingEmails,
		currentActiveDocument,
		currentActiveClient,
		currentActiveOpportunity,
		clientList,
		taxRegionsList,
		userRole,
		userId,
		companyUserList,
		clientContactList
	} = useSelector((state: GlobalState) => {
		return {
			allMergeTags:
				state.documents.currentActiveDocument
					?.merge_tag_system_variables ??
				state.emailThread.emailMergeTagsSystemVariable,
			documentId: state.documents.currentActiveDocument?.id,
			allRecipients: state.documents.currentActiveDocument?.recipients,
			allSignees: state.documents.currentActiveDocument?.signers,
			recipientsMissingEmails:
				state.documents.currentActiveDocument?.recipients.filter(
					recipient => !recipient.email || recipient.email === ""
				),
			currentActiveDocument: state.documents.currentActiveDocument,
			currentActiveClient: state.clients.currentActiveClient,
			currentActiveOpportunity:
				state.opportunities.currentActiveOpportunity,
			taxRegionsList: state.user.taxRegionsList,
			userRole: state.user.userData.role,
			userId: state.user.userData.id,
			companyUserList: state.user.companyUserList,
			clientList: state.clients.clientList,
			clientContactList: state.clients.clientContactList
		};
	});

	const [mergeTagPreference, setMergeTagPreference] =
		useState<MergeTagPreference>(MergeTagPreference.BLANK);
	const [mergeTagInputData, setMergeTagInputData] = useState<
		Record<string, any>
	>({});
	const [emptySignatureBox, setSignatureBox] = useState<Block[]>([]);
	const [emptyTextBox, setTextBox] = useState<Block[]>([]);
	const [emptySignatureBoxWithInactiveUser, setSignatureBoxWithInactiveUser] =
		useState<Block[]>([]);
	const [emptyTextBoxWithInactiveUser, setTextBoxWithInactiveUser] = useState<
		Block[]
	>([]);
	const [signeeForEmptySignBlock, setSigneeForEmptySignBlock] =
		useState<string>("");
	const [signeeForEmptyInputFields, setSigneeForEmptyInputFields] =
		useState<string>("");
	const [signeeForInactiveUserSignBlock, setSigneeForInactiveUserSignBlock] =
		useState<string>("");
	const [
		signeeForInactiveUserInputFields,
		setSigneeForInactiveUserInputFields
	] = useState<string>("");
	const [
		approverForApprovalsWithInactiveApprover,
		setApproverForApprovalsWithInactiveApprover
	] = useState("");
	const [buildEmptyMergeTagData, setBuildEmptyMergeTagData] = useState([]);
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [recipientList, setRecipientList] =
		useState<ClientContact[]>(clientContactList);
	const [isClientsLoading, setIsClientsLoading] = useState<boolean>(false);
	const [
		areMandatorySystemFieldsValuesEmpty,
		setAreMandatorySystemFieldsValuesEmpty
	] = useState<boolean>(false);
	const [areCustomFieldsValuesEmpty, setAreCustomFieldsValuesEmpty] =
		useState<boolean>(false);
	const isTaxregionEmptyForQuoteBlockDocument: boolean =
		!currentActiveDocument?.tax_region_id?.length &&
		!!currentActiveDocument?.revisions?.filter(
			revisions => !!revisions?.block_list?.length
		)?.length;

	const inactiveUserIds = companyUserList
		.filter(user => user.is_disabled)
		.map(user => user.id);

	const approvalRulesWithInactiveApprovers =
		currentActiveDocument?.approval_required_rules?.filter(rule =>
			rule.approver_automation_details.approver_user_ids?.find(id =>
				inactiveUserIds.includes(id)
			)
		) ?? [];

	const dispatch = useDispatch();
	const [form] = Form.useForm();

	const isFilterV2Enabled = useFeatureFlag("approval_rule_filter_v2");

	useEffect(() => {
		if (!props.visible) {
			return;
		}
		if (currentActiveDocument) {
			setAreMandatorySystemFieldsValuesEmpty(
				checkMandatoryDocumentSystemFields(currentActiveDocument)
			);
			setAreCustomFieldsValuesEmpty(
				currentActiveDocument.custom_fields?.some(
					customField =>
						!customField.values &&
						customField.mandatory_field_details?.for_ui
				) ?? false
			);
		}
		getClients();
		fetchClientContacts({
			filters: new Array<TableQueryFilter>(),
			page: 1,
			limit: 50,
			clientId: clientCompanyId
		});
	}, [props.visible, currentActiveDocument]);

	const handleNumberInputKeyDown = (
		e: React.KeyboardEvent<HTMLInputElement>
	) => {
		if (e.key === ".") {
			e.preventDefault();
			return;
		}
	};

	const renderInputFieldForMergeTag = (record: MergeTagVariable) => {
		if (record?.is_editable)
			switch (record?.data_type) {
				case MergeTagVariableDataType.DATE:
					return (
						<ZDatePicker
							className="w-full"
							onChange={e =>
								handleMergeTagInput(record, convertToISODate(e))
							}
						/>
					);
				case MergeTagVariableDataType.DOUBLE:
					return (
						<ZInputNumber
							placeholder="Enter Value"
							width="full"
							disabled={!record?.is_editable}
							onChange={e => handleMergeTagInput(record, e)}
						/>
					);
				case MergeTagVariableDataType.NUMBER:
					return (
						<ZInputNumber
							placeholder="Enter Value"
							width="full"
							disabled={!record?.is_editable}
							min={0}
							step={1}
							onChange={e => handleMergeTagInput(record, e)}
							onKeyDown={handleNumberInputKeyDown}
						/>
					);
				case MergeTagVariableDataType.DROPDOWN:
					return (
						<ZSelect
							getPopupContainer={trigger =>
								window.document.querySelector(".z-table") ||
								trigger.parentNode
							}
							onChange={(e: string) =>
								handleMergeTagInput(record, e)
							}
							placeholder="Select Value"
							className="w-full"
							selectOptions={record?.allowed_values ?? []}
						/>
					);
				default:
					return (
						<ZInput
							width="full"
							placeholder="Enter Value"
							disabled={!record?.is_editable}
							onChange={e =>
								handleMergeTagInput(record, e.target.value)
							}
						/>
					);
			}
		else
			return (
				<ZInput width="full" placeholder="No data available" disabled />
			);
	};

	const emptyMergeTagColumn = [
		{
			title: "Merge Tags",
			dataIndex: "display_name",
			key: "display_name",
			render: (merge_tag: string) => {
				return (
					<div className="ant-legacy-form-item-required">
						<ZText className="bg-yellow-2 px-1">{`{{${merge_tag}}}`}</ZText>
					</div>
				);
			}
		},
		{
			title: "Values",
			dataIndex: "record",
			key: "record",
			render: (_text: unknown, record: MergeTagVariable) =>
				renderInputFieldForMergeTag(record)
		}
	];

	const handleRadioButtonChange = (value: MergeTagPreference) => {
		setMergeTagPreference(value);

		if (value === MergeTagPreference.ENTRY) {
			const emptyMergeTagData: any = [];
			props.emptyMergeTags.map(tag => {
				allMergeTags?.map(merge_tag => {
					if (
						tag === merge_tag.variable_name ||
						tag === merge_tag.display_name
					) {
						emptyMergeTagData.push(merge_tag);
					}
				});
			});
			setBuildEmptyMergeTagData(emptyMergeTagData);
		}
	};

	const handleEmptyTextBox = async (
		textBlocks: Block[],
		assignee: string
	) => {
		if (currentActiveDocument?.is_single_block) {
			const editorManager = CKEditorInstanceManager.getInstance();
			if (!editorManager.isReady()) {
				return;
			}

			const unassignedFieldsResult =
				editorManager.getUnassignedInputFields(
					currentActiveDocument?.signers
				);
			const assigneeName = filter(allRecipients, recipient => {
				return recipient.id === assignee;
			});

			if (!assigneeName?.length) {
				return;
			}

			const unassignedFieldIds = new Set(
				unassignedFieldsResult.unassignedFields
					.map(f => f.fieldId)
					.filter(Boolean)
			);

			const editor = editorManager.getEditor();
			if (editor) {
				editor.model.change((writer: any) => {
					const root = editor.model.document.getRoot();
					const range = writer.createRangeIn(root);
					const itemsToUpdate: any[] = [];

					for (const item of range.getItems()) {
						if (item.is && item.is("element", "inputField")) {
							const fieldId = item.getAttribute("data-field-id");
							const fieldAssignee = item.getAttribute(
								"data-field-assignee"
							);

							const shouldUpdate =
								unassignedFieldIds.size > 0
									? unassignedFieldIds.has(fieldId)
									: !fieldAssignee ||
									  fieldAssignee === "undefined" ||
									  fieldAssignee === "";

							if (shouldUpdate) {
								const currentAttributes: any = {};
								for (const [
									key,
									value
								] of item.getAttributes()) {
									currentAttributes[key] = value;
								}

								itemsToUpdate.push({
									element: item,
									position: writer.createPositionBefore(item),
									attributes: {
										...currentAttributes,
										"data-field-assignee": assignee,
										title: `Text Assigned to ${assigneeName[0]?.name.full}`
									}
								});
							}
						}
					}

					itemsToUpdate.forEach(
						({ element, position, attributes }) => {
							writer.remove(element);
							const newElement = writer.createElement(
								"inputField",
								attributes
							);
							writer.insert(newElement, position);
						}
					);
				});
			}
			return;
		}

		// Update all textBlocks for non-single-block documents (TinyMCE)
		await Promise.all(
			textBlocks.map(async textBlock => {
				const blockData = { ...(textBlock.data as TextBlockData) };
				const assigneeName = filter(allRecipients, receipent => {
					return receipent.id === assignee;
				});

				const tinyMCETextBlock = window.tinyMCE?.get(textBlock.id);
				if (!!assigneeName?.length) {
					// check for field-assignee

					const htmlElem = document.createElement("html");
					htmlElem.innerHTML = blockData?.text;

					const inputFieldSelector =
						'[class="input-field mceNonEditable"]';
					const inputFieldElements =
						htmlElem.querySelectorAll(inputFieldSelector);

					Array.from(inputFieldElements).forEach(
						inputFieldElement => {
							const htmlElement =
								inputFieldElement as HTMLElement;
							const { fieldAssignee } = htmlElement.dataset;
							if (
								!fieldAssignee ||
								fieldAssignee === "undefined"
							) {
								htmlElement.dataset.fieldAssignee = assignee;
								htmlElement.title = `Text Assigned to ${assigneeName?.[0]?.name.full}`;
							}
						}
					);

					const htmlString = htmlElem.innerHTML;

					const newTextBlock = {
						...textBlock,
						data: {
							...blockData,
							text: htmlString
						}
					};

					try {
						await proposalBuilderDispatch(
							BlocksActionCreators.updateBlock(newTextBlock)
						);
						if (tinyMCETextBlock) {
							tinyMCETextBlock.setContent(htmlString);
						}
					} catch (error) {
						console.error(error);
					}
				}
			})
		);
	};

	// The signee select is only supposed to be shown if there is atleast 1 signature block present in the document
	// If there is unassigned input field and no signature block: Don't show the select
	const showSigneeSelect =
		props.isInputFieldMissing &&
		props.showEmptyFields &&
		emptySignatureBox?.length + emptyTextBox?.length > 0;

	const showActiveRecipientSelect =
		props.isInactiveUserPresent &&
		props.showEmptyFields &&
		emptySignatureBoxWithInactiveUser.length +
			emptyTextBoxWithInactiveUser.length >
			0;

	const showApproverSelect =
		props.showEmptyFields && !!approvalRulesWithInactiveApprovers?.length;

	const handleMissingInputFields = async () => {
		if (!showSigneeSelect) {
			return true;
		}

		if (
			!signeeForEmptySignBlock.length &&
			!signeeForEmptyInputFields.length
		) {
			ZMessage.error("Please select an Assignee");
			return false;
		} else {
			try {
				if (!currentActiveDocument?.id) {
					console.warn("No currentActiveDocument present");
					return;
				}

				// Update all sign blocks
				if (emptySignatureBox.length) {
					const bulkAssignSignatureBlocksRequest =
						new BulkAssignSignatureBlocksRequest({
							signer_id: signeeForEmptySignBlock,
							block_ids: emptySignatureBox.map(block => block.id)
						});

					await dispatch(
						DocumentsActionCreators.bulkAssignSignatureBlocks(
							bulkAssignSignatureBlocksRequest,
							currentActiveDocument?.id
						)
					);
				}

				await handleEmptyTextBox(
					emptyTextBox,
					signeeForEmptyInputFields
				);

				return true;
			} catch (error) {
				console.error(error);
				return false;
			}
		}
	};

	const handleUpdateMergeTags = async () => {
		if (
			props.isMergeTagMissing &&
			mergeTagPreference === MergeTagPreference.ENTRY &&
			(documentId ||
				!!currentActiveOpportunity?.id?.length ||
				!!currentActiveClient?.id?.length)
		) {
			const mergeTagsPayload: MergeTagInput = {
				entity_id:
					documentId ??
					currentActiveOpportunity?.id ??
					currentActiveClient?.id ??
					"",
				entity_type: documentId
					? ZomentumEntities.Document
					: !!currentActiveOpportunity?.id?.length
					? ZomentumEntities.Opportunity
					: !!currentActiveClient?.id?.length
					? ZomentumEntities.Client
					: ZomentumEntities.Document,
				merge_tags: []
			};

			props.emptyMergeTags?.find(tag => {
				mergeTagsPayload.merge_tags.push({
					key: tag,
					value: mergeTagInputData[tag] ?? ""
				});
			});

			try {
				await dispatch(
					DocumentsActionCreators.updateMergeTagsValue(
						mergeTagsPayload
					)
				);
				ZMessage.success("Merge tags updated");
			} catch (error) {
				console.error(error);
				setIsSubmitting(false);
				return;
			}
		}
	};

	const handleInactiveApprover = async () => {
		if (!approvalRulesWithInactiveApprovers.length) {
			return true;
		}
		if (!approverForApprovalsWithInactiveApprover.length) {
			ZMessage.error("Please select an approver");
			return false;
		}
		try {
			await Promise.all(
				approvalRulesWithInactiveApprovers.map(async approvalRule => {
					const {
						id,
						filter_criteria,
						is_enabled,
						title,
						description,
						filter_criteria_combiner,
						approver_automation_details: {
							approver_user_ids,
							document_approver_type
						},
						filter_groups
					} = approvalRule;

					const documentApprovalRuleEditRequest: DocumentApprovalUpdateRequest =
						{
							id,
							filter_criteria,
							filter_groups,
							is_enabled,
							title,
							description,
							filter_criteria_combiner,
							document_approver_type,
							approver_user_ids: uniq([
								...(approver_user_ids?.filter(
									id => !inactiveUserIds.includes(id)
								) ?? []),
								approverForApprovalsWithInactiveApprover
							])
						};

					try {
						await dispatch(
							DocumentApprovalRuleActionCreators.updateDocumentApprovalRule(
								documentApprovalRuleEditRequest,
								isFilterV2Enabled ? "v3" : "v2"
							)
						);
					} catch (error) {
						console.error(error);
					}
				})
			);
			currentActiveDocument &&
				(await dispatch(
					DocumentsActionCreators.getDocumentDetails({
						documentId: currentActiveDocument.id
					})
				));
			return true;
		} catch (error) {
			console.error(error);
			return false;
		}
	};

	const handleInactiveUserSignBlock = async () => {
		if (!props.isInactiveUserPresent) {
			return true;
		}
		if (
			!signeeForInactiveUserSignBlock.length &&
			!signeeForInactiveUserInputFields.length
		) {
			ZMessage.error("Please select a Recipient");
			return false;
		}
		// Update all sign blocks
		const bulkAssignSignatureBlocksRequest =
			new BulkAssignSignatureBlocksRequest({
				signer_id: signeeForInactiveUserSignBlock,
				block_ids: emptySignatureBoxWithInactiveUser.map(
					block => block.id
				)
			});

		try {
			if (!currentActiveDocument?.id) {
				console.warn("No currentActiveDocument present");
				return;
			}

			await dispatch(
				DocumentsActionCreators.bulkAssignSignatureBlocks(
					bulkAssignSignatureBlocksRequest,
					currentActiveDocument?.id
				)
			);

			await handleEmptyTextBox(
				emptyTextBoxWithInactiveUser,
				signeeForInactiveUserInputFields
			);

			await proposalBuilderDispatch(
				BlocksActionCreators.updateDocumentBlockMapper()
			);

			return true;
		} catch (error) {
			console.error(error);
			return false;
		}
	};

	const handleSubmit = async (
		editDocumentFormValues: MissingMandatoryFieldsFormValues,
		documentResponse: DocumentDetailsSuccessfulAction | null
	) => {
		let isSubmitSuccessful;

		if (!currentActiveDocument || !documentResponse) {
			ZMessage.error("No active document");
			isSubmitSuccessful = false;
			return isSubmitSuccessful;
		}

		if (!editDocumentFormValues) {
			ZMessage.error("Please fill all the values");
			isSubmitSuccessful = false;
			return isSubmitSuccessful;
		}

		let updatedDocument: EditDocumentDetailsRequest = {
			...documentResponse.currentActiveDocument
		};

		if (areMandatorySystemFieldsValuesEmpty) {
			updatedDocument.name =
				editDocumentFormValues.name ?? currentActiveDocument.name;

			updatedDocument.client_company_id =
				editDocumentFormValues.client_company_id ??
				currentActiveDocument.client_company_id;

			updatedDocument.tax_region_id =
				editDocumentFormValues.tax_region_id ??
				currentActiveDocument.tax_region_id;

			updatedDocument.user_id =
				editDocumentFormValues.user_id ?? currentActiveDocument.user_id;

			updatedDocument.recipients =
				editDocumentFormValues.recipients?.map(
					(recipientId: string) => {
						const existingRecipient =
							currentActiveDocument?.recipients.find(
								recipient => recipient.id === recipientId
							);

						if (existingRecipient) {
							return {
								id: existingRecipient.id,
								blocks_to_sign:
									existingRecipient.blocks_to_sign,
								user_type: existingRecipient.user_type
							};
						}

						const isRecipientUser = companyUserList.find(
							recipient => recipient.id === recipientId
						);

						if (isRecipientUser) {
							return {
								id: recipientId,
								blocks_to_sign: [],
								user_type: UserType.User
							};
						}

						return {
							id: recipientId,
							blocks_to_sign: [],
							user_type: UserType.ClientUser
						};
					}
				) ?? currentActiveDocument?.recipients;
		}

		if (areCustomFieldsValuesEmpty) {
			const customFieldFormValues =
				editDocumentFormValues.custom_field_form_values;
			if (
				customFieldFormValues &&
				!!currentActiveDocument.custom_fields?.length
			) {
				updatedDocument = {
					...updatedDocument,
					custom_fields: processCustomFieldFormValues(
						customFieldFormValues,
						currentActiveDocument.custom_fields
					)
				};
			}
			try {
				const editDocumentResponse = await dispatch(
					DocumentsActionCreators.editDocument(
						new EditDocumentRequest(
							updatedDocument,
							new Array<ChildEntityHydrationParams>(
								ChildEntityHydrationParams.CLIENT_COMPANIES_NAME,
								ChildEntityHydrationParams.OPPORTUNITY_NAME,
								ChildEntityHydrationParams.BLOCKS_CURRENT_REVISION,
								ChildEntityHydrationParams.TAX,
								ChildEntityHydrationParams.COMPANIES_ALL
							)
						)
					)
				);
				await dispatch(
					DocumentsActionCreators.updateDocumentDetails(
						editDocumentResponse.editDocument
					)
				);
				isSubmitSuccessful = true;
			} catch (error) {
				console.error(error);
				isSubmitSuccessful = false;
			}
		}

		if (!!editDocumentFormValues?.email_fields_form_values) {
			const values: ClientContactBulkUpdate[] = Object.keys(
				editDocumentFormValues?.email_fields_form_values
			).map(id => ({
				id,
				entity: editDocumentFormValues?.email_fields_form_values?.[id]
			}));

			if (!values || !values.length) {
				isSubmitSuccessful = true;
				return;
			}

			const payload: ClientContactBulkUpdateRequest = {
				data: [...values]
			};

			// Email validation for the entered emails on FE
			const emailRecipientValues = payload?.data?.map(
				email => email.entity.email
			);

			if (
				new Set(emailRecipientValues).size !==
				emailRecipientValues?.length
			) {
				isSubmitSuccessful = false;
				ZMessage.error(
					"Another contact with the same email id is already present in this client."
				);
				return isSubmitSuccessful;
			}

			try {
				const { results } = await dispatch(
					ClientsActionCreators.bulkUpdateClientContactEmail(payload)
				);

				const updatedClientContacts: ClientContact[] = [];

				// Email validation from network request
				results.forEach(value => {
					if (value.status === CommonServerStatusEnum.Failure) {
						ZMessage.error(value.response, true, 3);
						isSubmitSuccessful = false;
						return;
					} else {
						updatedClientContacts.push(
							value.response as ClientContact
						);
					}
					if (updatedClientContacts.length === results?.length) {
						if (updatedClientContacts?.length) {
							const newDocumentRecipients = [
								...currentActiveDocument.recipients
							];
							updatedClientContacts.forEach(
								updatedClientContact => {
									const idx = newDocumentRecipients.findIndex(
										clientContact =>
											clientContact.id ===
											updatedClientContact.id
									);
									if (idx >= 0) {
										newDocumentRecipients[idx] =
											DocumentRecipient.fromClientUser(
												updatedClientContact
											);
									} else {
										newDocumentRecipients.push(
											DocumentRecipient.fromClientUser(
												updatedClientContact
											)
										);
									}
								}
							);

							updatedDocument = {
								...updatedDocument,
								recipients: newDocumentRecipients
							};
						}
					}
				});
				isSubmitSuccessful = true;
			} catch (error) {
				console.error(error);
				isSubmitSuccessful = false;
			}
		}
		if (isSubmitSuccessful === false) {
			return isSubmitSuccessful;
		}

		try {
			// Will dispatch action to update document recipients
			dispatch(
				DocumentsActionCreators.updateDocumentDetails(updatedDocument)
			);
			isSubmitSuccessful = true;
		} catch (error) {
			console.error(error);
			isSubmitSuccessful = false;
		}
		return isSubmitSuccessful;
	};

	const onSubmit = async () => {
		setIsSubmitting(true);
		const inactiveUserSignatureAndInputFieldsHandled =
			await handleInactiveUserSignBlock();
		const inactiveApproverHandled = await handleInactiveApprover();
		const missingInputFieldsHandled = await handleMissingInputFields();

		if (
			!missingInputFieldsHandled ||
			!inactiveUserSignatureAndInputFieldsHandled ||
			!inactiveApproverHandled
		) {
			setIsSubmitting(false);
			return;
		}

		await handleUpdateMergeTags();

		let documentResponse: DocumentDetailsSuccessfulAction | null = null;
		// updating currentActiveDocument with latest data
		if (currentActiveDocument) {
			documentResponse = await dispatch(
				DocumentsActionCreators.getDocumentDetails({
					documentId: currentActiveDocument.id
				})
			);
		}

		if (
			(props.isMandatoryFieldsMissing &&
				(areCustomFieldsValuesEmpty ||
					areMandatorySystemFieldsValuesEmpty)) ||
			(props.isRecipientEmailMissing && !!recipientsMissingEmails?.length)
		) {
			let values: MissingMandatoryFieldsFormValues;

			try {
				values = await form.validateFields();
			} catch (error) {
				console.warn(error);
				setIsSubmitting(false);
				return;
			}

			const isSubmitSuccessful = await handleSubmit(
				values,
				documentResponse
			);

			if (!isSubmitSuccessful) {
				setIsSubmitting(false);
				return;
			} else {
				setIsSubmitting(false);
				props.confirmFromModal(mergeTagPreference);
				return;
			}
		}

		setIsSubmitting(false);
		props.confirmFromModal(mergeTagPreference);
	};

	const handleMergeTagInput = (
		tag: MergeTagVariable | null,
		value?: string | number | null
	) => {
		if (!tag) {
			return;
		}
		value = value || value === 0 ? ("" + value).trim() : null;
		setMergeTagInputData({
			...mergeTagInputData,
			[tag.variable_name]: value ? value : null,
			[tag.display_name]: value ? value : null
		});
	};

	const findEmptyOrInactiveFields = () => {
		if (currentActiveDocument?.is_single_block) {
			const editorManager = CKEditorInstanceManager.getInstance();
			if (!editorManager.isReady()) {
				setTextBox([]);
				setSignatureBox([]);
				return;
			}

			const { unassignedFields } = editorManager.getUnassignedInputFields(
				currentActiveDocument?.signers
			);

			if (unassignedFields.length > 0) {
				const virtualTextBlock = {
					id: "virtual-text-block",
					type: BlockType.TEXT,
					data: {
						text: ""
					} as TextBlockData,
					draft: false
				} as Block;
				setTextBox([virtualTextBlock]);
			} else {
				setTextBox([]);
			}

			const signBlocks = filter(props.updatedBlockMapper || {}, block => {
				return (
					block.type === BlockType.SIGNATURE &&
					!block.draft &&
					!currentActiveDocument?.recipients.find(recipient =>
						recipient.blocks_to_sign.includes(block.id)
					)
				);
			});
			setSignatureBox(signBlocks);

			setTextBoxWithInactiveUser([]);
			setSignatureBoxWithInactiveUser([]);
			return;
		}

		if (!props.updatedBlockMapper) {
			console.warn("No blocks are present");
			return;
		}

		const signBlocks = filter(props.updatedBlockMapper, block => {
			return (
				block.type === BlockType.SIGNATURE &&
				!block.draft &&
				!currentActiveDocument?.recipients.find(recipient =>
					recipient.blocks_to_sign.includes(block.id)
				)
			);
		});
		setSignatureBox(signBlocks);

		const isTextBlockHavingEmptyInputFields = (
			textBlockData: TextBlockData
		) => {
			const html = document
				.createRange()
				.createContextualFragment(textBlockData?.text);
			const inputFieldSelector = '[class="input-field mceNonEditable"]';
			const inputFieldElements =
				html.querySelectorAll(inputFieldSelector);
			return Array.from(inputFieldElements).some(inputFieldElement => {
				const htmlElement = inputFieldElement as HTMLElement;
				const recipientId = htmlElement.dataset.fieldAssignee;
				return !recipientId || recipientId === "undefined";
			});
		};

		const textBlocks = filter(props.updatedBlockMapper, block => {
			const blockData = block.data as TextBlockData;
			return (
				block.type === BlockType.TEXT &&
				(isTextBlockHavingEmptyInputFields(blockData) ||
					blockData.text.indexOf('title="To be assigned"') !== -1)
			);
		});
		setTextBox(textBlocks);

		const { signBlocksWithInactiveSignees, textBlocksWithInactiveSignees } =
			getSigneesAndInputFieldsWithInactiveUser(
				props.updatedBlockMapper,
				currentActiveDocument,
				inactiveUserIds
			);
		setSignatureBoxWithInactiveUser(signBlocksWithInactiveSignees);
		setTextBoxWithInactiveUser(textBlocksWithInactiveSignees);
	};

	const handleNewSigneeForEmptySignBlocks = (value: string) => {
		setSigneeForEmptySignBlock(value);
		if (signeeForEmptySignBlock === signeeForEmptyInputFields) {
			setSigneeForEmptyInputFields("");
		}
	};

	const handleNewSigneeForEmptyInputFields = (value: string) => {
		setSigneeForEmptyInputFields(value);
	};

	const handleNewSigneeForInactiveSignBlocks = (value: string) => {
		setSigneeForInactiveUserSignBlock(value);
		if (
			signeeForInactiveUserSignBlock === signeeForInactiveUserInputFields
		) {
			setSigneeForInactiveUserInputFields("");
		}
	};

	const handleNewSigneeForInactiveInputFields = (value: string) => {
		setSigneeForInactiveUserInputFields(value);
	};

	const handleNewApproverForInactiveApprovals = (
		value: string | string[]
	) => {
		setApproverForApprovalsWithInactiveApprover(value as string);
	};

	useEffect(() => {
		if (
			(props.isInputFieldMissing || props.isInactiveUserPresent) &&
			props.showEmptyFields &&
			props.visible
		) {
			findEmptyOrInactiveFields();
		} else {
			setMergeTagPreference(MergeTagPreference.BLANK);
			setMergeTagInputData({});
			setBuildEmptyMergeTagData([]);
			setSigneeForEmptySignBlock("");
			setSigneeForEmptyInputFields("");
		}
	}, [props.visible, props.isInputFieldMissing]);

	const onCancel = () => {
		form.resetFields();
		props.closeModal();
	};

	const isSignatureBlockInDocument =
		!!props.updatedBlockMapper &&
		!!Object.values(props.updatedBlockMapper).find(
			block =>
				block.type === BlockType.SIGNATURE &&
				!block.draft &&
				(props.currentActiveDocument?.is_single_block
					? props.currentActiveDocument?.revisions?.[0]?.block_list?.includes(
							block.id
					  )
					: true)
		);

	const clientCompanyId = currentActiveDocument?.client_company_id ?? "";

	const handleRecipientsSearch = debounce((value: string) => {
		const clientContactRequest: ClientContactRequest = {
			filters: new Array<TableQueryFilter>(),
			page: 1,
			limit: 50,
			clientId: clientCompanyId
		};

		if (value) {
			clientContactRequest.keywords = value;
		}

		fetchClientContacts(clientContactRequest);
	}, 300);

	const fetchClientContacts = async (
		clientContactRequest: ClientContactRequest
	) => {
		if (!clientContactRequest.clientId) {
			return;
		}
		const clientUsersResponse = await dispatch(
			ClientsActionCreators.getClientContacts(clientContactRequest)
		);
		setRecipientList(clientUsersResponse.clientContactList);
	};

	const getClients = async () => {
		setIsClientsLoading(true);
		const clientsRequest: ClientsRequest = new ClientsRequest(
			new Array<TableQueryFilter>(),
			null,
			100,
			new Array<ChildEntityHydrationParams>()
		);
		try {
			await dispatch(ClientsActionCreators.getClients(clientsRequest));
		} catch (error) {
			console.error(error);
		}
		setIsClientsLoading(false);
	};

	const onChangeClient = async (clientId: string) => {
		if (clientId) {
			const clientContactRequest = new ClientContactRequest(clientId);

			await ClientsActionCreators.getClientContacts(clientContactRequest);

			await fetchClientContacts(clientContactRequest);
			form.setFieldsValue({
				recipients: undefined
			});
		}
	};
	const getFormFields = (): IFormFieldProps[] => {
		const formFields: IFormFieldProps[] = [];
		if (!currentActiveDocument?.name.length) {
			formFields.push({
				key: "name",
				label: "Title",
				required: true,
				message: "Please enter document name",
				initialValue: undefined,
				widget: EWidgets.INPUT,
				widgetProps: {
					width: EInputWidths.full,
					placeholder: "Please enter document name"
				}
			});
		}
		if (!currentActiveDocument?.recipients?.length) {
			formFields.push({
				key: "recipients",
				label: "Recipients",
				required: true,
				message: "Please choose a recipient",
				widget: EWidgets.SELECT,
				widgetProps: {
					mode: "multiple",
					allowSearch: true,
					onSearch: handleRecipientsSearch,
					placeholder: "Please choose a recipient",
					isOptionsGroup: true,
					optionsGroup: [
						{
							label: "Contacts",
							selectOptions: recipientList?.map(
								clientContact => ({
									name: clientContact.name.full,
									value: clientContact.id
								})
							)
						},
						{
							label: "Users",
							selectOptions: companyUserList.map(user => ({
								name: user.is_disabled
									? `${user.name.full} (Inactive user)`
									: user.name.full,
								value: user.id,
								disabled: user.is_disabled
							}))
						}
					]
				}
			});
		}
		if (!currentActiveDocument?.client_company_name?.length) {
			formFields.push({
				key: "client_company_name",
				label: "Client",
				required: true,
				message: "Please choose a client",
				initialValue: undefined,
				widget: ({ value, onChange }) => (
					<ZClientSelect
						placeholder="Please choose a client"
						loading={isClientsLoading}
						value={value}
						defaultOptions={clientList}
						onChange={clientId => {
							onChangeClient(clientId);
							onChange(clientId);
						}}
					/>
				)
			});
		}
		if (isTaxregionEmptyForQuoteBlockDocument) {
			formFields.push({
				key: "tax_region_id",
				label: "Tax Region",
				required: true,
				message: "Please select tax region",
				initialValue: undefined,
				widget: EWidgets.SELECT,
				widgetProps: {
					allowClear: true,
					placeholder: "Please select tax region",
					selectOptions: taxRegionsList
						?.filter(taxRegion => !taxRegion.disabled)
						?.map(taxRegion => ({
							name: taxRegion.name,
							value: taxRegion.id
						}))
				}
			});
		}
		if (!currentActiveDocument?.user_id?.length) {
			formFields.push({
				key: "user_id",
				label: "Owner",
				required: true,
				message: "Please select owner",
				initialValue: undefined,
				widget: ({ value, onChange }) => (
					<ZUserSelect
						showSearch={true}
						allowClear={true}
						loading={!userId || !companyUserList}
						onChange={onChange}
						value={value}
						disabled={
							userRole &&
							ManagerAndAdminRoleOnly.indexOf(userRole) === -1
								? true
								: false
						}
						placeholder="Please select owner"
						filterOption={filterSelectByTextInput}
					/>
				)
			});
		}
		formFields.push({
			key: "custom_fields",
			render: () =>
				currentActiveDocument?.custom_fields?.map(
					field =>
						!field?.values &&
						field?.mandatory_field_details?.for_ui && (
							<div key={field.id}>
								<ZRenderCustomFieldFormItem
									form={form}
									customField={field}
									values={field.values}
									formAlignment={[9, 15]}
								/>
							</div>
						)
				)
		});
		return formFields;
	};

	const formProps: IFormProps = {
		key: "mandatoryFieldsForm",
		name: "mandatoryFieldsForm",
		formLayout: {
			labelCol: { span: 9 },
			wrapperCol: { span: 15 }
		},
		sections: [
			{
				meta: {
					fields: getFormFields()
				}
			}
		],
		form: form,
		isSubmitAllowed: false,
		preserve: false,
		isDynamic: true
	};

	const isAnyMergeTagInputEmpty = useMemo(
		() => !!props.emptyMergeTags?.find(tag => !mergeTagInputData[tag]),
		[mergeTagInputData, props.emptyMergeTags]
	);

	const isOkButtonDisabled =
		mergeTagPreference === MergeTagPreference.ENTRY &&
		isAnyMergeTagInputEmpty;

	const recipientOptions = allRecipients
		?.filter(recipient => !inactiveUserIds.includes(recipient.id))
		?.map(signee => ({
			name: capitalizeFirstLetter(signee.name.full),
			value: signee.id
		}));

	const selectedSignee =
		recipientOptions?.filter(
			recipient =>
				recipient.value === signeeForEmptySignBlock ||
				recipient.value === signeeForInactiveUserSignBlock
		) ?? [];

	const signeeOptions = [
		...(allSignees
			?.filter(signee => !inactiveUserIds.includes(signee.id))
			?.map(signee => ({
				name: capitalizeFirstLetter(signee.name.full),
				value: signee.id
			})) ?? []),
		...selectedSignee
	];

	return (
		<ZModal
			title=""
			closable
			centered
			size="md"
			confirmLoading={props.isLoading || isSubmitting}
			destroyOnClose
			visible={props.visible}
			onCancel={onCancel}
			okText={`${props.confirmText}`}
			okButtonProps={{ disabled: isOkButtonDisabled }}
			onOk={onSubmit}
		>
			<div className="mt-n4">
				{showActiveRecipientSelect && (
					<>
						<ZSectionHeading className="pt-6">
							Assign Active User
						</ZSectionHeading>
						<ZText colorVariant={7} className="block mb-2">
							Few fields are assigned to inactive users:
						</ZText>
						{!!emptySignatureBoxWithInactiveUser?.length && (
							<>
								<ZText className="block mt-4 mb-2">
									<ZText weight={FontWeights.MEDIUM}>
										{
											emptySignatureBoxWithInactiveUser?.length
										}
									</ZText>{" "}
									Signature Block
									{emptySignatureBoxWithInactiveUser?.length ===
									1
										? ""
										: "s"}
								</ZText>
								<ZText colorVariant={7} className="block mb-2">
									Select a recipient to assign the signature
									block to:
								</ZText>
								<ZSelect
									loading={
										allRecipients &&
										allRecipients?.length === 0
									}
									placeholder="Select recipient"
									className="w-full"
									value={signeeForInactiveUserSignBlock}
									onChange={
										handleNewSigneeForInactiveSignBlocks
									}
									disabled={!isSignatureBlockInDocument}
									selectOptions={recipientOptions}
									data-testid="inactive-signature-select"
								/>
							</>
						)}
						{emptyTextBoxWithInactiveUser?.length > 0 && (
							<>
								<ZText className="block mt-4 mb-2">
									<ZText weight={FontWeights.MEDIUM}>
										{emptyTextBoxWithInactiveUser?.length}
									</ZText>{" "}
									Text Block
									{emptyTextBoxWithInactiveUser?.length === 1
										? ""
										: "s"}
								</ZText>
								<ZText colorVariant={7} className="block mb-2">
									Select a recipient to assign all{" "}
									{emptyTextBoxWithInactiveUser?.length}{" "}
									remaining fields to:
								</ZText>
								<ZSelect
									loading={
										allRecipients &&
										allRecipients?.length === 0
									}
									placeholder="Select recipient"
									className="w-full"
									value={signeeForInactiveUserInputFields}
									onChange={
										handleNewSigneeForInactiveInputFields
									}
									disabled={!isSignatureBlockInDocument}
									selectOptions={signeeOptions}
								/>
							</>
						)}
					</>
				)}
				{showApproverSelect && (
					<>
						<ZText className="block mt-4 mb-2">Approver</ZText>
						<ZUserSelect
							placeholder="Select Approver"
							className="w-full"
							onChange={handleNewApproverForInactiveApprovals}
							data-testid="inactive-approver-select"
						/>
					</>
				)}
				{showSigneeSelect && (
					<div data-testid="empty-fields-section">
						<ZSectionHeading className="pt-6">
							Empty Fields
						</ZSectionHeading>
						{emptySignatureBox?.length > 0 && (
							<>
								<ZText className="block mb-2">
									<ZText colorVariant={7} className="block">
										One or more signature boxes are not
										assigned in
									</ZText>
									<ZText weight={FontWeights.MEDIUM}>
										{emptySignatureBox?.length}
									</ZText>{" "}
									Signature Block
									{emptySignatureBox?.length === 1 ? "" : "s"}
								</ZText>
								<ZSelect
									getPopupContainer={trigger =>
										trigger.parentNode
									}
									loading={
										allRecipients &&
										allRecipients?.length === 0
									}
									placeholder="Select recipient"
									className="w-full mb-4"
									value={signeeForEmptySignBlock}
									onChange={handleNewSigneeForEmptySignBlocks}
									disabled={!isSignatureBlockInDocument}
									selectOptions={recipientOptions}
								/>
							</>
						)}
						{emptyTextBox?.length > 0 && (
							<>
								<ZText className="block mb-2">
									<ZText colorVariant={7} className="block">
										One or more input fields are not
										assigned in
									</ZText>
									<ZText weight={FontWeights.MEDIUM}>
										{emptyTextBox?.length}
									</ZText>{" "}
									Text Block
									{emptyTextBox?.length === 1 ? "" : "s"}
								</ZText>
								<ZSelect
									getPopupContainer={trigger =>
										trigger.parentNode
									}
									loading={
										allRecipients &&
										allRecipients?.length === 0
									}
									placeholder="Select recipient"
									className="w-full"
									value={signeeForEmptyInputFields}
									onChange={
										handleNewSigneeForEmptyInputFields
									}
									disabled={!isSignatureBlockInDocument}
									selectOptions={signeeOptions}
								/>
							</>
						)}

						{!isSignatureBlockInDocument && (
							<ZText colorVariant={7} className="block my-2">
								No signee present. Please add a signature block
								and assign, to assign the input field
							</ZText>
						)}
					</div>
				)}

				{props?.emptyMergeTags && !!props.emptyMergeTags.length && (
					<>
						<ZSectionHeading className="pt-6">
							Empty Merge Tags
						</ZSectionHeading>

						{props.emptyMergeTags && (
							<ZText colorVariant={7} className="mb-2 block">
								Total merge tags without assigned values:{" "}
								<ZText weight={FontWeights.MEDIUM}>
									{props.emptyMergeTags.length}
								</ZText>
							</ZText>
						)}

						{mergeTagPreference !== MergeTagPreference.ENTRY && (
							<div className="mb-2">
								{props.emptyMergeTags?.map((tag, index) => {
									const resolvedLabel = allMergeTags.find(
										mergeTag =>
											mergeTag.variable_name === tag ||
											tag === mergeTag.display_name
									);

									return (
										<ZText
											className="bg-yellow-2 mr-2 px-1"
											key={index}
										>{`{{${
											resolvedLabel?.display_name ?? tag
										}}}`}</ZText>
									);
								})}
							</div>
						)}

						<ZRadioGroup
							onChange={e =>
								handleRadioButtonChange(e.target.value)
							}
							value={mergeTagPreference}
							vertical
							options={[
								{
									label: "Replace all empty merge tags with blank",
									value: MergeTagPreference.BLANK
								},
								{
									label: "Do not replace (merge tags will be visible)",
									value: MergeTagPreference.REPLACE
								},
								{
									label: "Individual value entry for unassigned merge tags",
									value: MergeTagPreference.ENTRY
								}
							]}
							className="mb-4"
						/>

						{mergeTagPreference === MergeTagPreference.ENTRY &&
							!!buildEmptyMergeTagData?.length && (
								<ZTable
									dataSource={buildEmptyMergeTagData}
									columns={emptyMergeTagColumn}
									size="small"
									bordered
									pagination={false}
									scroll={{
										y: "200px"
									}}
									rowKey={record => record.display_name}
									isTopBorder={false}
									loading={false}
								/>
							)}
					</>
				)}

				{props.isRecipientEmailMissing &&
					!!recipientsMissingEmails?.length && (
						<>
							<ZSectionHeading className="pt-6">
								Missing Email
							</ZSectionHeading>
							<MissingDetailsSection
								form={form}
								missingEmailKeys={
									recipientsMissingEmails
										? recipientsMissingEmails.map(
												recipient => ({
													id: recipient.id,
													name: recipient.name.full
												})
										  )
										: []
								}
								visible={!!recipientsMissingEmails?.length}
								subtitle="For sending the Document, you need to add emails for the following recipients:"
							/>
						</>
					)}

				{props.isMandatoryFieldsMissing &&
					(areCustomFieldsValuesEmpty ||
						areMandatorySystemFieldsValuesEmpty) && (
						<div className="mt-2">
							<ZSectionHeading className="mb-2">
								Empty Mandatory Fields
							</ZSectionHeading>
							<ZText colorVariant={7}>
								Following mandatory fields are not assigned
								values:
							</ZText>
							<ZForm {...formProps} className="mt-2" />
						</div>
					)}
			</div>
		</ZModal>
	);
};

export default EmptyMergeTagsAlertModal;
